import { useMemo } from 'react'

import useMutation from 'use-mutation'

import { request, jsonError } from 'modules'
import { useNotification } from './use-notification'

export const useDataMutation = <Input, Data, E extends Error>(
    url: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    callback?: {
        onSuccess?: (data: Data, input: Input) => void
        onFailure?: (error: E, input: Input) => void
        disableDefaultErrorHandling?: boolean
    }
) => {
    const { showErrorNotification } = useNotification()

    const [mutate, { status, data, error, reset }] = useMutation<Input, Data, E>(
        body => request(url, method, body),
        {
            onSettled(params) {
                const { status, input } = params

                switch (status) {
                    default:
                    case 'success': {
                        return callback?.onSuccess?.(params.data, input)
                    }
                    case 'failure': {
                        if (!callback?.disableDefaultErrorHandling) {
                            showErrorNotification(
                                params.error.message ||
                                    'Something went wrong. Please try again later'
                            )
                        }

                        if (params.error.message === jsonError) {
                            showErrorNotification(
                                'Uh-Oh! Please check your Internet connection and try again.'
                            )
                        }

                        return callback?.onFailure?.(params.error, input)
                    }
                }
            },
            throwOnFailure: true
        }
    )

    const isLoading = status === 'running'

    return useMemo(
        () => ({ mutate, isLoading, data, error, reset }),
        [data, error, isLoading, mutate, reset]
    )
}
